import { Box, Typography } from '@mui/material';
import { Fragment } from 'react';
import { sections } from '../../data/homepage';

/**
 * UpdatesHeading - "Research. Stories. Media. Community." banner copy.
 *
 * The first letter of every word is picked out in brand green so the words
 * spell RSMC. Words and the intro line come from the CMS
 * (src/content/homepage.json → sections.updates) and are shared by the
 * homepage section and the /updates page, which only differ in size.
 *
 * @param {Object} props - Component props
 * @param {'h1'|'h2'} [props.component] - Heading element to render
 * @param {Object} [props.fontSize] - Responsive font size for the heading
 * @param {string} [props.introFontSize] - Font size for the intro line
 * @returns {JSX.Element} Centered heading and intro line
 */
function UpdatesHeading({
  component = 'h2',
  fontSize = { xs: '2rem', md: '2.5rem' },
  introFontSize = '1.125rem',
}) {
  const { headingWords, intro } = sections.updates;

  return (
    <Box sx={{ textAlign: 'center', px: { xs: 2, md: 4 } }}>
      <Typography
        variant={component}
        component={component}
        sx={{
          color: 'var(--color-secondary-text)',
          fontWeight: 400,
          fontSize,
          fontFamily: '"Playfair Display", serif',
          mb: 2,
        }}
        aria-label={headingWords.map((word) => `${word}.`).join(' ')}
      >
        {headingWords.map((word, index) => (
          <Fragment key={word}>
            {index > 0 && ' '}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: 'var(--color-primary)' }}
              aria-hidden="true"
            >
              {word.charAt(0)}
            </Box>
            <Box component="span" aria-hidden="true">
              {word.slice(1)}.
            </Box>
          </Fragment>
        ))}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: introFontSize,
          maxWidth: '900px',
          mx: 'auto',
        }}
      >
        {intro}
      </Typography>
    </Box>
  );
}

export default UpdatesHeading;
